import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchTeachers,
  fetchCampuses,
  fetchTeacherCourseAssignments,
  mergeTeacherCourseAssignments,
  createTeacher,
  updateTeacher,
  deleteTeacher,
  findTeacherByCnic,
  ALS_DATA_CHANGED_EVENT,
} from "./dataService";
import TeacherForm from "./TeacherForm";
import { printReport } from "../../shared/reports/reportExport";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";

/* ═══════════════════════════════════════════════════════════════════════
   Design tokens — ALS Portal palette
   ═══════════════════════════════════════════════════════════════════════ */
const C = {
  navy: "#0f1b2d",
  blue: "#1d4ed8",
  blueSoft: "#e8f0fe",
  red: "#dc2626",
  redSoft: "#fff5f5",
  green: "#16a34a",
  greenSoft: "#f0fff4",
  amber: "#d97706",
  amberSoft: "#fffbeb",
  slate: "#64748b",
  slateLight: "#94a3b8",
  border: "#e2e8f0",
  bg: "#f4f6f9",
  white: "#ffffff",
  text: "#1a202c",
};

const inputSt = {
  padding: "9px 13px",
  borderRadius: "8px",
  border: `1.5px solid ${C.border}`,
  fontSize: "13px",
  color: C.text,
  background: C.white,
  outline: "none",
  boxSizing: "border-box",
};

const labelSt = {
  display: "block",
  fontSize: "11px",
  fontWeight: 700,
  color: C.slate,
  marginBottom: "5px",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
};

/* ─── Small reusable UI primitives ─── */

const Btn = ({
  children,
  onClick,
  disabled,
  variant = "primary",
  style: extra = {},
}) => {
  const variants = {
    primary: { background: C.navy, color: "#fff", border: "none" },
    ghost: {
      background: C.white,
      color: C.slate,
      border: `1.5px solid ${C.border}`,
    },
    green: { background: C.green, color: "#fff", border: "none" },
    danger: { background: C.red, color: "#fff", border: "none" },
  };
  const v = variants[variant] || variants.primary;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "9px 18px",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        whiteSpace: "nowrap",
        transition: "opacity .15s",
        ...v,
        ...extra,
      }}
    >
      {children}
    </button>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    Active: [C.greenSoft, C.green],
    "On Leave": [C.amberSoft, C.amber],
    Resigned: ["#f1f5f9", C.slate],
    Terminated: [C.redSoft, C.red],
  };
  const [bg, color] = map[status] || ["#f1f5f9", C.slate];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: 700,
        background: bg,
        color,
      }}
    >
      {status || "—"}
    </span>
  );
};

const TH = ({ children, center }) => (
  <th
    style={{
      textAlign: center ? "center" : "left",
      padding: "10px 16px",
      fontSize: "11px",
      fontWeight: 700,
      color: C.slate,
      background: C.bg,
      borderBottom: `2px solid ${C.border}`,
      textTransform: "uppercase",
      letterSpacing: "0.4px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);

const TD = ({ children, center, mono }) => (
  <td
    style={{
      padding: "12px 16px",
      fontSize: "13px",
      color: C.text,
      borderBottom: `1px solid ${C.border}`,
      verticalAlign: "middle",
      textAlign: center ? "center" : "left",
      fontFamily: mono ? "monospace" : undefined,
    }}
  >
    {children}
  </td>
);

const DetailRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "3px",
      padding: "10px 0",
      borderBottom: `1px solid ${C.border}`,
    }}
  >
    <span
      style={{
        fontSize: "10px",
        fontWeight: 700,
        color: C.slate,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
      }}
    >
      {label}
    </span>
    <span style={{ fontSize: "13px", color: C.text, fontWeight: 500 }}>
      {value || "—"}
    </span>
  </div>
);

/* ─── Toast overlay ─── */

const ToastOverlay = ({ toast, onClose }) => {
  if (!toast) return null;
  const colors = {
    success: [C.greenSoft, C.green],
    error: [C.redSoft, C.red],
    info: [C.blueSoft, C.blue],
  };
  const [bg, accent] = colors[toast.type] || colors.info;
  return (
    <div
      style={{
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 9999,
        animation: "slideIn .3s ease",
      }}
    >
      <div
        style={{
          background: bg,
          border: `1.5px solid ${accent}`,
          borderRadius: "10px",
          padding: "14px 20px",
          maxWidth: "360px",
          boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
        }}
      >
        <div style={{ flex: 1 }}>
          <strong style={{ color: accent, fontSize: "13px" }}>
            {toast.title}
          </strong>
          <div style={{ fontSize: "12px", color: C.text, marginTop: "3px" }}>
            {toast.message}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: C.slate,
            fontSize: "16px",
            padding: 0,
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

/* ─── Confirm / Delete modal ─── */

const ConfirmModal = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  onConfirm,
  onCancel,
  danger = false,
}) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: C.white,
          borderRadius: "12px",
          padding: "28px",
          maxWidth: "440px",
          width: "92%",
          boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: 800,
            color: C.text,
            margin: "0 0 10px 0",
          }}
        >
          {title}
        </h3>
        <p style={{ fontSize: "13px", color: C.slate, margin: "0 0 22px 0" }}>
          {message}
        </p>
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <Btn variant="ghost" onClick={onCancel}>
            Cancel
          </Btn>
          <Btn variant={danger ? "danger" : "primary"} onClick={onConfirm}>
            {confirmLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
};

/* ─── Status-change modal ─── */

const StatusChangeModal = ({ open, currentStatus, onConfirm, onCancel }) => {
  const [chosen, setChosen] = useState("");
  const allStatuses = ["Active", "On Leave", "Resigned", "Terminated"];
  const options = allStatuses.filter((s) => s !== currentStatus);

  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: C.white,
          borderRadius: "12px",
          padding: "28px",
          maxWidth: "400px",
          width: "92%",
          boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: 800,
            color: C.text,
            margin: "0 0 6px 0",
          }}
        >
          Change Employment Status
        </h3>
        <p
          style={{
            fontSize: "13px",
            color: C.slate,
            margin: "0 0 18px 0",
          }}
        >
          Current status: <StatusBadge status={currentStatus} />
        </p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: "22px",
          }}
        >
          {options.map((s) => (
            <label
              key={s}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 14px",
                borderRadius: "8px",
                border: `1.5px solid ${chosen === s ? C.blue : C.border}`,
                background: chosen === s ? C.blueSoft : C.white,
                cursor: "pointer",
                transition: "all .15s",
              }}
            >
              <input
                type="radio"
                name="newStatus"
                value={s}
                checked={chosen === s}
                onChange={() => setChosen(s)}
                style={{ accentColor: C.blue }}
              />
              <StatusBadge status={s} />
            </label>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <Btn variant="ghost" onClick={onCancel}>
            Cancel
          </Btn>
          <Btn
            variant="primary"
            onClick={() => chosen && onConfirm(chosen)}
            disabled={!chosen}
          >
            Update Status
          </Btn>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Full-page teacher detail view — enhanced with Edit / Delete / Status
   ═══════════════════════════════════════════════════════════════════════ */
const TeacherDetailPage = ({
  teacher,
  campusName,
  onBack,
  onEdit,
  onDelete,
  onStatusChange,
  canAssignCredentials,
  canEdit,
}) => {
  const [docs, setDocs] = useState(
    Array.isArray(teacher.documents) ? teacher.documents : [],
  );

  useEffect(() => {
    const loadDocs = async () => {
      if (!teacher?.teacher_id) return;
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/teachers/${encodeURIComponent(teacher.teacher_id)}/documents`,
          { headers: getAuthHeaders() },
        );
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) setDocs(data);
        }
      } catch (err) {
        console.error("Failed to load teacher documents", err);
      }
    };
    loadDocs();
  }, [teacher?.teacher_id]);

  if (!teacher) return null;
  const assignedCourses = Array.isArray(teacher.assigned_courses)
    ? teacher.assigned_courses
    : [];

  const printTeacherDetails = () => {
    const dash = (v) =>
      v == null || String(v).trim() === "" ? "—" : String(v);
    const subjects = Array.isArray(teacher.subjects) ? teacher.subjects : [];
    const salary =
      teacher.salary != null
        ? `PKR ${Number(teacher.salary).toLocaleString()}`
        : "—";

    const sections = [
      {
        heading: "Personal Details",
        pairs: [
          ["Teacher Name", dash(teacher.name)],
          ["Father's / Husband's Name", dash(teacher.father_name)],
          ["Gender", dash(teacher.gender)],
          ["Date of Birth", dash(teacher.date_of_birth)],
          ["CNIC", dash(teacher.cnic)],
          ["Contact Number", dash(teacher.contact_number)],
          ["Email", dash(teacher.email)],
          ["Address", dash(teacher.address)],
        ],
      },
      {
        heading: "Employment Details",
        pairs: [
          ["Campus", dash(campusName)],
          ["Qualification", dash(teacher.qualification)],
          ["Experience (Years)", dash(teacher.experience_years)],
          ["Joining Date", dash(teacher.joining_date)],
          ["Employment Status", dash(teacher.employment_status)],
          ["Salary (PKR)", salary],
        ],
      },
      {
        heading: "Subjects Taught",
        pairs: [["Subjects", subjects.length ? subjects.join(", ") : "—"]],
      },
    ];

    if (assignedCourses.length > 0) {
      sections.push({
        heading: "Assigned Courses",
        headers: ["Class", "Course"],
        rows: assignedCourses.map((c) => [
          c.class_name || "—",
          c.course_name || "—",
        ]),
      });
    } else {
      sections.push({
        heading: "Assigned Courses",
        pairs: [
          ["Courses", "No courses have been assigned to this teacher yet."],
        ],
      });
    }

    sections.push({
      heading: "Emergency Contact",
      pairs: [
        ["Contact Name", dash(teacher.emergency_contact_name)],
        ["Contact Number", dash(teacher.emergency_contact_number)],
      ],
    });

    const documents = Array.isArray(docs) ? docs : [];
    sections.push({
      heading: "Documents",
      pairs: documents.length
        ? documents.map((doc) => [
            doc.name || doc.file_name || "Document",
            [doc.type, doc.file_name || doc.url].filter(Boolean).join(" · ") || "—",
          ])
        : [["Documents", "No documents uploaded yet."]],
    });

    printReport({
      title: `Teacher Details — ${teacher.name || "Teacher"}`,
      campus: campusName || "",
      scopeMeta: [
        ["Teacher", dash(teacher.name)],
        ["Campus", dash(campusName)],
        ["Status", dash(teacher.employment_status)],
        ["CNIC", dash(teacher.cnic)],
      ],
      summaryCards: [],
      sections,
    });
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      {/* Back + title row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <button
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 38,
            height: 38,
            borderRadius: "9px",
            border: `1.5px solid ${C.border}`,
            background: C.white,
            cursor: "pointer",
            color: C.slate,
            flexShrink: 0,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          ←
        </button>
        <h1
          style={{
            fontSize: "20px",
            fontWeight: 800,
            color: C.text,
            margin: 0,
          }}
        >
          Teacher Details
        </h1>
      </div>

      <div
        style={{
          background: C.white,
          borderRadius: "12px",
          border: `1px solid ${C.border}`,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          overflow: "hidden",
          // maxWidth: "920px",
        }}
      >
        {/* Profile strip */}
        <div
          style={{
            background: C.navy,
            padding: "26px 28px",
            display: "flex",
            alignItems: "center",
            gap: "18px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "26px",
              flexShrink: 0,
            }}
          >
            {(teacher.name || "?").charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: "20px", color: "#fff" }}>
              {teacher.name}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "rgba(255,255,255,0.65)",
                marginTop: "3px",
              }}
            >
              {campusName}
            </div>
            <div
              style={{
                marginTop: "10px",
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <StatusBadge status={teacher.employment_status} />
            </div>
          </div>
          {/* Action buttons in the header strip */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              marginLeft: "auto",
            }}
          >
            <Btn
              variant="ghost"
              onClick={printTeacherDetails}
              style={{
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "1.5px solid rgba(255,255,255,0.25)",
              }}
            >
              🖨 Print
            </Btn>
            {canEdit && (
              <>
                <Btn
                  variant="ghost"
                  onClick={onEdit}
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    border: "1.5px solid rgba(255,255,255,0.25)",
                  }}
                >
                  ✏️ Edit
                </Btn>
                <Btn
                  variant="ghost"
                  onClick={onStatusChange}
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    border: "1.5px solid rgba(255,255,255,0.25)",
                  }}
                >
                  🔄 Change Status
                </Btn>
                <Btn
                  variant="ghost"
                  onClick={onDelete}
                  style={{
                    background: "rgba(220,38,38,0.15)",
                    color: "#fca5a5",
                    border: "1.5px solid rgba(220,38,38,0.35)",
                  }}
                >
                  🗑 Delete
                </Btn>
              </>
            )}
          </div>
        </div>

        {/* Body content */}
        <div style={{ padding: "24px 28px" }}>
          {/* Personal Details */}
          <div
            style={{
              fontSize: "12px",
              fontWeight: 800,
              color: C.slate,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "6px",
            }}
          >
            Personal Details
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "0 40px",
              marginBottom: "20px",
            }}
          >
            <DetailRow
              label="Father's / Husband's Name"
              value={teacher.father_name}
            />
            <DetailRow label="Gender" value={teacher.gender} />
            <DetailRow label="Date of Birth" value={teacher.date_of_birth} />
            <DetailRow label="CNIC" value={teacher.cnic} />
            <DetailRow label="Contact Number" value={teacher.contact_number} />
            <DetailRow label="Email" value={teacher.email} />
            <DetailRow label="Address" value={teacher.address} />
          </div>

          {/* Employment Details */}
          <div
            style={{
              fontSize: "12px",
              fontWeight: 800,
              color: C.slate,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "6px",
            }}
          >
            Employment Details
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "0 40px",
              marginBottom: "20px",
            }}
          >
            <DetailRow label="Campus" value={campusName} />
            <DetailRow label="Qualification" value={teacher.qualification} />
            <DetailRow
              label="Experience (years)"
              value={teacher.experience_years}
            />
            <DetailRow label="Joining Date" value={teacher.joining_date} />
            <DetailRow
              label="Employment Status"
              value={teacher.employment_status}
            />
            <DetailRow
              label="Salary (PKR)"
              value={
                teacher.salary != null
                  ? `PKR ${Number(teacher.salary).toLocaleString()}`
                  : undefined
              }
            />
          </div>

          {/* ─── Credentials Section ─── */}
          <div
            style={{
              fontSize: "12px",
              fontWeight: 800,
              color: C.slate,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "6px",
            }}
          >
            System Login Credentials
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "0 40px",
              marginBottom: "20px",
              background: canAssignCredentials ? C.blueSoft : C.redSoft,
              borderRadius: "8px",
              padding: "14px 18px",
              border: `1px solid ${canAssignCredentials ? C.blue : C.red}`,
            }}
          >
            {teacher.login_email ? (
              <>
                <DetailRow
                  label="Login Email (Username)"
                  value={teacher.login_email}
                />
                <DetailRow
                  label="Password"
                  value={
                    canAssignCredentials
                      ? "Set at registration — not displayed for security"
                      : "⛔ You do not have permission to view credential details"
                  }
                />
              </>
            ) : (
              <div
                style={{
                  fontSize: "13px",
                  color: C.slate,
                  padding: "10px 0",
                }}
              >
                No login credentials have been assigned to this teacher yet.
                {canAssignCredentials && (
                  <span style={{ color: C.blue, fontWeight: 600 }}>
                    {" "}
                    Click "Edit" to assign credentials.
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Subjects Taught */}
          <div
            style={{
              fontSize: "12px",
              fontWeight: 800,
              color: C.slate,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "6px",
            }}
          >
            Subjects Taught
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginBottom: "20px",
            }}
          >
            {(Array.isArray(teacher.subjects) ? teacher.subjects : [])
              .length === 0 ? (
              <span style={{ fontSize: "13px", color: C.slate }}>—</span>
            ) : (
              teacher.subjects.map((s) => (
                <span
                  key={s}
                  style={{
                    display: "inline-block",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: 700,
                    background: C.blueSoft,
                    color: C.blue,
                  }}
                >
                  {s}
                </span>
              ))
            )}
          </div>

          {/* Assigned Courses */}
          <div
            style={{
              fontSize: "12px",
              fontWeight: 800,
              color: C.slate,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "6px",
            }}
          >
            Assigned Courses
          </div>
          {assignedCourses.length === 0 ? (
            <div
              style={{
                fontSize: "13px",
                color: C.slate,
                marginBottom: "20px",
              }}
            >
              No courses have been assigned to this teacher yet.
            </div>
          ) : (
            <div
              style={{
                overflowX: "auto",
                marginBottom: "20px",
                border: `1px solid ${C.border}`,
                borderRadius: "8px",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <TH>Class</TH>
                    <TH>Course</TH>
                  </tr>
                </thead>
                <tbody>
                  {assignedCourses.map((c, i) => (
                    <tr
                      key={`${c.course_id}-${i}`}
                      style={{
                        background: i % 2 === 0 ? C.white : "#fafbfd",
                      }}
                    >
                      <TD>{c.class_name || "—"}</TD>
                      <TD>{c.course_name || "—"}</TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Emergency Contact */}
          <div
            style={{
              fontSize: "12px",
              fontWeight: 800,
              color: C.slate,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "6px",
            }}
          >
            Emergency Contact
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "0 40px",
            }}
          >
            <DetailRow
              label="Contact Name"
              value={teacher.emergency_contact_name}
            />
            <DetailRow
              label="Contact Number"
              value={teacher.emergency_contact_number}
            />
          </div>

          {/* Documents uploaded by the teacher */}
          <div
            style={{
              fontSize: "12px",
              fontWeight: 800,
              color: C.slate,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              margin: "20px 0 6px",
            }}
          >
            Documents
          </div>
          {Array.isArray(docs) && docs.length > 0 ? (
            <div
              style={{
                display: "grid",
                gap: "8px",
              }}
            >
              {docs.map((doc, index) => {
                const path = doc.url || doc.file_path || "";
                const href = path
                  ? path.startsWith("http")
                    ? path
                    : `${API_BASE_URL}${path}`
                  : "";
                return (
                  <div
                    key={doc.id || index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      padding: "10px 12px",
                      border: `1px solid ${C.border}`,
                      borderRadius: "8px",
                      background: "#fafbfd",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "13px" }}>
                        {doc.name || doc.file_name || "Document"}
                      </div>
                      <div style={{ fontSize: "12px", color: C.slate }}>
                        {[doc.type, doc.file_name].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          fontSize: "12px",
                          fontWeight: 700,
                          color: C.blue,
                          textDecoration: "none",
                        }}
                      >
                        View
                      </a>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: "13px", color: C.slate }}>
              No documents uploaded yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Main list + CRUD page
   ═══════════════════════════════════════════════════════════════════════ */
function TeacherReportsPage({ onLogout }) {
  const navigate = useNavigate();

  /* ─── Data state ─── */
  const [teachers, setTeachers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  /* ─── Filter state ─── */
  const [searchTerm, setSearchTerm] = useState("");
  const [campusFilter, setCampusFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  /* ─── View / edit state ─── */
  const [viewTeacher, setViewTeacher] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);

  /* ─── Action modals ─── */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusChangeTarget, setStatusChangeTarget] = useState(null);

  /* ─── Toast ─── */
  const [toast, setToast] = useState(null);
  const showToast = useCallback((type, title, message) => {
    setToast({ type, title, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  /* ─── Role-based permissions ─── */
  const currentRole = (localStorage.getItem("als_role") || "").toLowerCase();
  const canAssignCredentials = ["superadmin", "principal", "admin"].includes(currentRole);
  const canEdit = ["superadmin", "principal", "admin"].includes(currentRole);
  const canDelete = ["superadmin", "principal", "admin"].includes(currentRole);

  /* ─── Data loading ─── */
  const loadData = useCallback(async () => {
    try {
      setLoadError(null);
      const [teacherList, campusList, assignmentList] = await Promise.all([
        fetchTeachers(),
        fetchCampuses(),
        fetchTeacherCourseAssignments().catch((err) => {
          console.warn("Could not load teacher course assignments", err);
          return [];
        }),
      ]);
      const merged = mergeTeacherCourseAssignments(
        Array.isArray(teacherList) ? teacherList : [],
        Array.isArray(assignmentList) ? assignmentList : [],
      );
      setTeachers(merged);
      setCampuses(Array.isArray(campusList) ? campusList : []);
    } catch (err) {
      if (err?.status === 401) {
        onLogout?.();
        return;
      }
      console.error("Failed to load teacher reports data", err);
      setLoadError("Couldn't load teacher reports. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    loadData();
    window.addEventListener("focus", loadData);
    window.addEventListener(ALS_DATA_CHANGED_EVENT, loadData);
    return () => {
      window.removeEventListener("focus", loadData);
      window.removeEventListener(ALS_DATA_CHANGED_EVENT, loadData);
    };
  }, [loadData]);

  /* ─── Helpers ─── */
  const normId = (v) => (v === null || v === undefined ? "" : String(v).trim());

  const campusNameById = useMemo(() => {
    const map = {};
    campuses.forEach((c) => {
      map[normId(c.id)] = c.name;
    });
    return map;
  }, [campuses]);

  const getCampusName = useCallback(
    (campusId) => campusNameById[normId(campusId)] || null,
    [campusNameById],
  );

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return teachers.filter((t) => {
      if (term) {
        const haystack = `${t.name || ""} ${t.cnic || ""} ${
          t.contact_number || ""
        } ${t.email || ""} ${t.login_email || ""}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (campusFilter && normId(t.campus_id) !== normId(campusFilter))
        return false;
      if (statusFilter && (t.employment_status || t.status) !== statusFilter)
        return false;
      return true;
    });
  }, [teachers, searchTerm, campusFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, campusFilter, statusFilter]);

  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize],
  );

  const clearFilters = () => {
    setSearchTerm("");
    setCampusFilter("");
    setStatusFilter("");
  };

  const unmatchedCampusCount = useMemo(
    () =>
      teachers.filter((t) => t.campus_id && !getCampusName(t.campus_id)).length,
    [teachers, getCampusName],
  );

  /* ─── CRUD handlers ─── */

  const handleSaveTeacher = async (payload) => {
    try {
      if (editingTeacher) {
        const updated = await updateTeacher(editingTeacher.teacher_id, payload);
        setTeachers((prev) =>
          prev.map((t) =>
            t.id === editingTeacher.id ? { ...t, ...(updated || payload) } : t,
          ),
        );
        showToast(
          "success",
          "Teacher Updated",
          `${payload.name}'s record was updated.`,
        );
        // If we were viewing this teacher, refresh the view
        if (viewTeacher && viewTeacher.id === editingTeacher.id) {
          setViewTeacher({
            ...viewTeacher,
            ...(updated || payload),
          });
        }
      } else {
        const duplicate = await findTeacherByCnic(payload.cnic);
        if (duplicate) {
          showToast(
            "error",
            "Already Registered",
            "A teacher with this CNIC is already registered.",
          );
          return false;
        }
        const created = await createTeacher(payload);
        setTeachers((prev) => [...prev, created]);
        showToast(
          "success",
          "Teacher Registered",
          `${payload.name} was added successfully.`,
        );
      }
      setShowForm(false);
      setEditingTeacher(null);
      return true;
    } catch (err) {
      console.error("Failed to save teacher", err);
      showToast(
        "error",
        "Save Failed",
        err.message || "Something went wrong while saving.",
      );
      return false;
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTeacher(deleteTarget.teacher_id);
      setTeachers((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      if (viewTeacher && viewTeacher.id === deleteTarget.id) {
        setViewTeacher(null);
      }
      showToast(
        "success",
        "Teacher Removed",
        "The teacher record was deleted.",
      );
    } catch (err) {
      console.error("Failed to delete teacher", err);
      showToast(
        "error",
        "Delete Failed",
        err.message || "Something went wrong while deleting.",
      );
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!statusChangeTarget) return;
    try {
      const payload = {
        employment_status: newStatus,
      };
      const updated = await updateTeacher(
        statusChangeTarget.teacher_id,
        payload,
      );
      setTeachers((prev) =>
        prev.map((t) =>
          t.id === statusChangeTarget.id
            ? {
                ...t,
                employment_status: newStatus,
                ...(updated || {}),
              }
            : t,
        ),
      );
      if (viewTeacher && viewTeacher.id === statusChangeTarget.id) {
        setViewTeacher({
          ...viewTeacher,
          employment_status: newStatus,
          ...(updated || {}),
        });
      }
      showToast(
        "success",
        "Status Updated",
        `${statusChangeTarget.name} is now "${newStatus}".`,
      );
    } catch (err) {
      console.error("Failed to change status", err);
      showToast("error", "Status Change Failed", "Something went wrong.");
    } finally {
      setStatusChangeTarget(null);
    }
  };

  const handleEditTeacher = (teacher) => {
    setEditingTeacher(teacher);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingTeacher(null);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingTeacher(null);
  };

  /* ─── Early-return states ─── */

  if (loading) {
    return (
      <div
        style={{
          background: C.bg,
          minHeight: "100vh",
          padding: "24px",
        }}
      >
        <p style={{ color: C.slate, fontSize: "13px" }}>
          Loading teacher reports…
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        style={{
          background: C.bg,
          minHeight: "100vh",
          padding: "24px",
        }}
      >
        <p
          style={{
            color: C.red,
            fontSize: "13px",
            marginBottom: "12px",
          }}
        >
          {loadError}
        </p>
        <Btn onClick={loadData}>Retry</Btn>
      </div>
    );
  }

  /* ─── Form overlay (add / edit) ─── */
  if (showForm) {
    return (
      <>
        <ToastOverlay toast={toast} onClose={() => setToast(null)} />
        <div
          style={{
            background: C.bg,
            minHeight: "100vh",
            padding: "24px",
            overflowY: "auto",
          }}
        >
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <TeacherForm
              key={editingTeacher ? editingTeacher.teacher_id : "new-teacher"}
              onSave={handleSaveTeacher}
              onCancel={handleCancelForm}
              initialData={editingTeacher}
              showToast={showToast}
              campuses={campuses}
              canAssignCredentials={canAssignCredentials}
            />
          </div>
        </div>
      </>
    );
  }

  /* ─── Detail view ─── */
  if (viewTeacher) {
    const campusName = getCampusName(viewTeacher.campus_id) || "Unknown Campus";

    return (
      <>
        <ToastOverlay toast={toast} onClose={() => setToast(null)} />
        <ConfirmModal
          open={deleteTarget !== null}
          title="Delete Teacher"
          message={`Are you sure you want to permanently delete "${deleteTarget?.name}"? This action cannot be undone.`}
          confirmLabel="Delete Permanently"
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTarget(null)}
          danger
        />
        <StatusChangeModal
          open={statusChangeTarget !== null}
          currentStatus={statusChangeTarget?.employment_status || ""}
          onConfirm={handleStatusChange}
          onCancel={() => setStatusChangeTarget(null)}
        />
        <TeacherDetailPage
          teacher={viewTeacher}
          campusName={campusName}
          onBack={() => setViewTeacher(null)}
          onEdit={() => handleEditTeacher(viewTeacher)}
          onDelete={() => setDeleteTarget(viewTeacher)}
          onStatusChange={() => setStatusChangeTarget(viewTeacher)}
          canAssignCredentials={canAssignCredentials}
          canEdit={canEdit}
        />
      </>
    );
  }

  /* ─── Main list view ─── */
  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      <ToastOverlay toast={toast} onClose={() => setToast(null)} />
      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete Teacher"
        message={`Are you sure you want to permanently delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete Permanently"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        danger
      />
      <StatusChangeModal
        open={statusChangeTarget !== null}
        currentStatus={statusChangeTarget?.employment_status || ""}
        onConfirm={handleStatusChange}
        onCancel={() => setStatusChangeTarget(null)}
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <button
          onClick={() => navigate("/dashboard")}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 38,
            height: 38,
            borderRadius: "9px",
            border: `1.5px solid ${C.border}`,
            background: C.white,
            cursor: "pointer",
            color: C.slate,
            flexShrink: 0,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          ←
        </button>
        <h1
          style={{
            fontSize: "20px",
            fontWeight: 800,
            color: C.text,
            margin: 0,
          }}
        >
          Teacher Management
        </h1>
        {canEdit && (
          <Btn
            variant="green"
            onClick={handleAddNew}
            style={{ marginLeft: "auto" }}
          >
            + Register Teacher
          </Btn>
        )}
      </div>

      {unmatchedCampusCount > 0 && (
        <div
          style={{
            background: C.amberSoft,
            border: `1px solid ${C.amber}`,
            borderRadius: "10px",
            padding: "12px 16px",
            marginBottom: "16px",
            fontSize: "13px",
            color: C.text,
          }}
        >
          <strong>{unmatchedCampusCount}</strong> teacher
          {unmatchedCampusCount !== 1 ? "s" : ""}
          {unmatchedCampusCount !== 1 ? "have" : "has"} a campus_id that doesn't
          match any current campus. Edit
          {unmatchedCampusCount !== 1 ? "them" : "that teacher"}
          to re-assign the correct campus.
        </div>
      )}

      <div
        style={{
          background: C.white,
          borderRadius: "12px",
          border: `1px solid ${C.border}`,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* Card header */}
        <div
          style={{
            background: C.navy,
            padding: "14px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#fff",
            }}
          >
            All Teachers
          </span>
          <span
            style={{
              fontSize: "12px",
              color: "rgba(255,255,255,0.8)",
              background: "rgba(255,255,255,0.12)",
              padding: "3px 12px",
              borderRadius: "20px",
              fontWeight: 600,
            }}
          >
            {filtered.length} teacher
            {filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Filters */}
        <div
          style={{
            padding: "14px 20px",
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            alignItems: "flex-end",
            borderBottom: `1px solid ${C.border}`,
            background: C.bg,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              flex: "1 1 220px",
            }}
          >
            <label style={labelSt}>Search</label>
            <input
              type="text"
              placeholder="Name, CNIC, contact, email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...inputSt, width: "100%" }}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              minWidth: "160px",
            }}
          >
            <label style={labelSt}>Campus</label>
            <select
              value={campusFilter}
              onChange={(e) => setCampusFilter(e.target.value)}
              style={inputSt}
            >
              <option value="">All Campuses</option>
              {campuses.map((c) => (
                <option key={normId(c.id)} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              minWidth: "150px",
            }}
          >
            <label style={labelSt}>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={inputSt}
            >
              <option value="">All Statuses</option>
              {["Active", "On Leave", "Resigned", "Terminated"].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          {(searchTerm || campusFilter || statusFilter) && (
            <Btn variant="ghost" onClick={clearFilters}>
              Clear Filters
            </Btn>
          )}
        </div>

        {/* Table / empty state */}
        {filtered.length === 0 ? (
          <div
            style={{
              padding: "70px 20px",
              textAlign: "center",
              color: C.slate,
            }}
          >
            <div
              style={{
                fontSize: "40px",
                marginBottom: "12px",
              }}
            >
              📭
            </div>
            <div
              style={{
                fontWeight: 700,
                fontSize: "15px",
                color: C.text,
                marginBottom: "6px",
              }}
            >
              {teachers.length === 0
                ? "No Teachers Registered"
                : "No Teachers Match Your Filters"}
            </div>
            <div style={{ fontSize: "13px" }}>
              {teachers.length === 0
                ? "Register a teacher to see them listed here."
                : "Try adjusting your search or filter criteria."}
            </div>
            {teachers.length > 0 && (
              <div style={{ marginTop: "14px" }}>
                <Btn variant="ghost" onClick={clearFilters}>
                  Clear Filters
                </Btn>
              </div>
            )}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  <TH>#</TH>
                  <TH>Name</TH>
                  <TH>Contact</TH>
                  <TH>Campus</TH>
                  <TH>Courses</TH>
                  <TH>Status</TH>
                  <TH>Credentials</TH>
                  <TH center>Actions</TH>
                </tr>
              </thead>
              <tbody>
                {paged.map((t, idx) => {
                  const rowBg = idx % 2 === 0 ? C.white : "#fafbfd";
                  const assignedCourses = Array.isArray(t.assigned_courses)
                    ? t.assigned_courses
                    : [];
                  const courseLabels = [
                    ...new Set(
                      assignedCourses
                        .map((c) => {
                          const name = (c.course_name || "").trim();
                          if (!name) return "";
                          const cls = (c.class_name || "").trim();
                          return cls ? `${name} (${cls})` : name;
                        })
                        .filter(Boolean),
                    ),
                  ];
                  const displayedCampus =
                    getCampusName(t.campus_id) ||
                    t.campus_name ||
                    t.campus_id ||
                    "—";
                  const statusLabel =
                    t.employment_status || t.status || "—";

                  return (
                    <tr
                      key={t.id}
                      style={{
                        background: rowBg,
                        cursor: "pointer",
                      }}
                      onClick={() => setViewTeacher(t)}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = C.blueSoft)
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = rowBg)
                      }
                    >
                      <TD>
                        <span
                          style={{
                            color: C.slateLight,
                            fontWeight: 600,
                            fontSize: "12px",
                          }}
                        >
                          {(currentPage - 1) * pageSize + idx + 1}
                        </span>
                      </TD>
                      <TD>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              background: C.navy,
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 700,
                              fontSize: "13px",
                              flexShrink: 0,
                            }}
                          >
                            {(t.name || "?").charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 700 }}>{t.name}</span>
                        </div>
                      </TD>
                      <TD>{t.contact_number || t.phone || "—"}</TD>
                      <TD>{displayedCampus}</TD>
                      <TD>
                        {courseLabels.length === 0 ? (
                          "—"
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                              maxWidth: 280,
                            }}
                          >
                            {courseLabels.map((label) => (
                              <span
                                key={label}
                                style={{
                                  display: "inline-block",
                                  padding: "3px 10px",
                                  borderRadius: "20px",
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  background: C.blueSoft,
                                  color: C.blue,
                                }}
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        )}
                      </TD>
                      <TD>
                        <StatusBadge status={statusLabel} />
                      </TD>
                      <TD>
                        {t.login_email ? (
                          <span
                            style={{
                              fontSize: "12px",
                              color: C.blue,
                              fontWeight: 600,
                            }}
                          >
                            {t.login_email}
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: "12px",
                              color: C.slateLight,
                            }}
                          >
                            None
                          </span>
                        )}
                      </TD>
                      <TD center>
                        <div
                          style={{
                            display: "inline-flex",
                            gap: "6px",
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewTeacher(t);
                            }}
                            title="View Details"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: "7px",
                              border: "none",
                              background: C.blue,
                              color: "#fff",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "14px",
                            }}
                          >
                            👁
                          </button>
                          {canEdit && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditTeacher(t);
                              }}
                              title="Edit Teacher"
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: "7px",
                                border: "none",
                                background: C.amber,
                                color: "#fff",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                              }}
                            >
                              ✏️
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(t);
                              }}
                              title="Delete Teacher"
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: "7px",
                                border: "none",
                                background: C.red,
                                color: "#fff",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                              }}
                            >
                              🗑
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusChangeTarget(t);
                              }}
                              title="Change Status"
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: "7px",
                                border: `1.5px solid ${C.border}`,
                                background: C.white,
                                color: C.slate,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                              }}
                            >
                              🔄
                            </button>
                          )}
                        </div>
                      </TD>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filtered.length > 0 && (
          <div
            style={{
              padding: "12px 20px",
              borderTop: `1px solid ${C.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
              background: C.bg,
            }}
          >
            <div style={{ fontSize: "12px", color: C.slate }}>
              Showing <strong>{(currentPage - 1) * pageSize + 1}</strong>–
              <strong>
                {Math.min(currentPage * pageSize, filtered.length)}
              </strong>{" "}
              of <strong>{filtered.length}</strong>
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  ...inputSt,
                  padding: "6px 10px",
                  width: "auto",
                }}
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
              <Btn
                variant="ghost"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ padding: "6px 14px" }}
              >
                ‹ Prev
              </Btn>
              <span
                style={{
                  fontSize: "12px",
                  color: C.slate,
                  fontWeight: 600,
                  minWidth: "90px",
                  textAlign: "center",
                }}
              >
                Page {currentPage} of {totalPages}
              </span>
              <Btn
                variant="ghost"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                style={{ padding: "6px 14px" }}
              >
                Next ›
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TeacherReportsPage;
